// src/config/ratingScale.ts
/*
 * What a rating scale is allowed to be, and what one resolves to when its
 * author has not chosen.
 *
 * `rating` is a configurable scale stored in the field's `validation` JSON
 * (`min`, `max`, and optional `minLabel`/`maxLabel`). `nps` is the fixed 0-10
 * with the standard anchors, which is why it is a second field type rather than
 * a rating preset: NPS carries semantics (0-6 detractor, 7-8 passive, 9-10
 * promoter) that a later reporting plan has to identify, and a rating with min
 * 0 and max 10 is not necessarily an NPS question.
 * See docs/field-types-and-quiz-gallery_plan.md Phase 2.
 *
 * These are product rules that do not vary per environment, so they are
 * constants here rather than environment variables (CLAUDE.md §2). One module
 * because the validator and the four renderers that switch on a field type all
 * need the same numbers, and a scale bound copied into five files is the drift
 * this plan exists to close.
 */

/**
 * Fewer than three points is a yes/no question, which `radio` already draws
 * better and labels properly; more than ten stops being distinguishable and
 * stops fitting across a phone. `nps` is exempt by definition: its eleven
 * points are the scale.
 */
export const RATING_MIN_POINTS = 3;
export const RATING_MAX_POINTS = 10;

/** The scale a rating field is drawn at when it carries no configuration. */
export const DEFAULT_RATING_MIN = 1;
export const DEFAULT_RATING_MAX = 5;

/** NPS asks 0 to 10. Fixed by the definition of the question. */
export const NPS_MIN = 0;
export const NPS_MAX = 10;
export const NPS_MIN_LABEL = "Not at all likely";
export const NPS_MAX_LABEL = "Extremely likely";

/** The `validation` JSON a rating field carries, as far as the scale is concerned. */
export type RatingScaleConfig = {
  min?: number | null;
  max?: number | null;
  minLabel?: string | null;
  maxLabel?: string | null;
} | null;

/** A resolved scale: the points to draw and the words at either end. */
export type RatingScale = {
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
};

/** How many points a scale from `min` to `max` draws, ends included. */
export function ratingPointCount(min: number, max: number): number {
  return max - min + 1;
}

/**
 * The scale a renderer should draw for a field.
 *
 * `nps` ignores whatever it was configured with: its scale is the definition of
 * the question, and a stored 2-to-3 "NPS" is data left behind by an editor that
 * did not know that, not an instruction.
 */
export function ratingScale(
  type: string | undefined,
  validation: RatingScaleConfig,
): RatingScale {
  if (type === "nps") {
    return {
      min: NPS_MIN,
      max: NPS_MAX,
      minLabel: NPS_MIN_LABEL,
      maxLabel: NPS_MAX_LABEL,
    };
  }

  const min =
    typeof validation?.min === "number" && Number.isFinite(validation.min)
      ? validation.min
      : DEFAULT_RATING_MIN;
  const max =
    typeof validation?.max === "number" && Number.isFinite(validation.max)
      ? validation.max
      : DEFAULT_RATING_MAX;

  const scale: RatingScale = { min, max };
  if (validation?.minLabel) scale.minLabel = validation.minLabel;
  if (validation?.maxLabel) scale.maxLabel = validation.maxLabel;
  return scale;
}

/**
 * The most points any renderer will draw.
 *
 * NPS is eleven, one past the rating maximum, and anything beyond that is a
 * scale nobody configured on purpose: `max` comes out of stored JSON, so a
 * broken import carrying `max: 1000000` would otherwise render a million
 * radios and hang the page.
 */
export const MAX_RENDERABLE_POINTS = Math.max(RATING_MAX_POINTS, 11);

/**
 * The points a scale draws, or `null` when the scale is unusable.
 *
 * Shared by all four renderers rather than reimplemented in each, so a scale
 * one of them refuses to draw is a scale all four refuse to draw. Warns naming
 * the field instead of throwing: one broken field must not take down the whole
 * form, and the name is what somebody needs to go and fix it.
 */
export function ratingPoints(
  scale: RatingScale,
  fieldName: string,
): number[] | null {
  const { min, max } = scale;
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    console.warn(
      `Rating scale: not drawing "${fieldName}", its scale is not numeric (min ${String(min)}, max ${String(max)}).`,
    );
    return null;
  }
  if (max <= min) {
    console.warn(
      `Rating scale: not drawing "${fieldName}", its scale ends where it starts or earlier (min ${min}, max ${max}).`,
    );
    return null;
  }
  const count = ratingPointCount(min, max);
  if (count > MAX_RENDERABLE_POINTS) {
    console.warn(
      `Rating scale: not drawing "${fieldName}", ${count} points is past the ${MAX_RENDERABLE_POINTS} this can draw (min ${min}, max ${max}).`,
    );
    return null;
  }
  return Array.from({ length: count }, (_, i) => min + i);
}
